import { useState, useEffect } from "react";
import { listUsers, createUser, updateUser, sendUserReset, listSmartewmClients, listSmartewmSapSystems } from "../lib/adminApi";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Switch } from "../components/ui/switch";
import { Checkbox } from "../components/ui/checkbox";
import { UserPlus, Pencil, Mail, ChevronDown, ChevronRight } from "lucide-react";
import EnvBadge, { ClientLogo } from "../components/EnvBadge";
import { toast } from "sonner";
import {
  APP_WHX, APP_SMARTEVALUATE, APP_SMARTBID, APP_SMARTIMPROVE, APP_SMARTEWM, APP_SMARTINSPECT,
  APPLICATION_CATALOGUE, WHX_CHILD_APPS,
} from "../lib/applications";

// --- Catalogue (must mirror admin_routes.py) ------------------------------
const PLATFORM_ROLE_OPTIONS = [
  { value: "admin", label: "Admin" },
  { value: "consultant", label: "Consultant" },
  { value: "demo", label: "Demo" },
  { value: "client", label: "Client" },
];

// SmartEWM is the only application whose Application Role drives behaviour.
const SMARTEWM_ROLE_OPTIONS = ["Operator", "Supervisor", "Manager", "Support", "Admin"];

const PLATFORM_ROLE_BADGE = {
  admin: "bg-[#E0F2FE] text-[#025979] border border-[#025979]/20",
  consultant: "bg-[#ECFDF5] text-[#047857] border border-[#047857]/20",
  demo: "bg-[#FEF3C7] text-[#92400E] border border-[#92400E]/20",
  client: "bg-[#EDE9FE] text-[#5B21B6] border border-[#5B21B6]/20",
};

const APP_BADGE = {
  [APP_WHX]: "bg-[#E0F2FE] text-[#025979] border border-[#025979]/20",
  [APP_SMARTEVALUATE]: "bg-[#ECFDF5] text-[#047857] border border-[#047857]/20",
  [APP_SMARTBID]: "bg-[#FFF1F2] text-[#9F1239] border border-[#9F1239]/20",
  [APP_SMARTIMPROVE]: "bg-[#FEF3C7] text-[#92400E] border border-[#92400E]/20",
  [APP_SMARTEWM]: "bg-[#DBEAFE] text-[#1E40AF] border border-[#1E40AF]/20",
  [APP_SMARTINSPECT]: "bg-[#F3E8FF] text-[#6B21A8] border border-[#6B21A8]/20",
};

const appSlug = (app) => app.toLowerCase().replace(/\s+/g, "-");

const emptyForm = {
  name: "",
  email: "",
  role: "consultant",
  active: true,
  send_invite: true,
  applications: [APP_WHX],
  application_role: "Supervisor",
  // Multi-Client access scoping (SmartEWM).
  allowedClientIds: [],
  defaultClientId: "",
  allowedSapSystems: [],
  defaultSapSystem: "",
};

const NONE = "__none__";

// --- Business-friendly display helpers ------------------------------------
// Users + admins should never see internal IDs (primes4 / PS4-EWM-DEV). We map
// IDs -> business names sourced entirely from the Client/SAP System Masters.
function clientLabel(clients, clientId) {
  const c = clients.find((x) => x.clientId === clientId);
  return c ? c.clientName : clientId;
}

function sapLabel(sapSystems, clientSapId) {
  const s = sapSystems.find((x) => x.clientSapId === clientSapId);
  // "SAP EWM DEV" — module + environment, no raw identifier.
  return s ? `SAP ${s.systemModule} ${s.environment}` : clientSapId;
}

function sapEnv(sapSystems, clientSapId) {
  const s = sapSystems.find((x) => x.clientSapId === clientSapId);
  return s ? s.environment : "";
}

// Compact summary for the Users table ("PrimeS4" / "Multiple" / "—").
function summarizeIds(ids, resolver) {
  const list = Array.isArray(ids) ? ids.filter(Boolean) : [];
  if (list.length === 0) return null;
  if (list.length === 1) return resolver(list[0]);
  return "Multiple";
}

export default function UsersPage() {
  const [users, setUsers] = useState([]);
  const [err, setErr] = useState("");
  const [addOpen, setAddOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [editing, setEditing] = useState(null);
  const [busy, setBusy] = useState(false);
  const [clients, setClients] = useState([]);
  const [sapSystems, setSapSystems] = useState([]);
  const [filterClient, setFilterClient] = useState(NONE);
  const [filterSap, setFilterSap] = useState(NONE);

  const load = () =>
    listUsers()
      .then(setUsers)
      .catch(() => setErr("Failed to load users"));

  useEffect(() => { load(); }, []);

  // Load the Multi-Client masters once so the access-scoping dropdowns are
  // populated. Failures are non-fatal — the section degrades to empty lists.
  useEffect(() => {
    listSmartewmClients()
      .then((d) => setClients(d?.clients || []))
      .catch(() => setClients([]));
    listSmartewmSapSystems()
      .then((d) => setSapSystems(d?.sapSystems || []))
      .catch(() => setSapSystems([]));
  }, []);

  const openAdd = () => {
    setForm(emptyForm);
    setAddOpen(true);
  };

  const openEdit = (u) => {
    setEditing(u);
    const apps =
      Array.isArray(u.applications) && u.applications.length
        ? u.applications
        : [u.application_access || APP_WHX];
    setForm({
      name: u.name || "",
      email: u.email || "",
      role: u.role || "consultant",
      active: u.active !== false,
      send_invite: false,
      applications: apps,
      application_role: u.application_role || "Supervisor",
      allowedClientIds: Array.isArray(u.allowedClientIds) ? u.allowedClientIds : [],
      defaultClientId: u.defaultClientId || "",
      allowedSapSystems: Array.isArray(u.allowedSapSystems) ? u.allowedSapSystems : [],
      defaultSapSystem: u.defaultSapSystem || "",
    });
    setEditOpen(true);
  };

  const updateForm = (patch) => {
    setForm((prev) => ({ ...prev, ...patch }));
  };

  const toggleApplication = (app) => {
    setForm((prev) => {
      const has = prev.applications.includes(app);
      const next = has
        ? prev.applications.filter((a) => a !== app)
        : [...prev.applications, app];
      return { ...prev, applications: next };
    });
  };

  const submitAdd = async (e) => {
    e.preventDefault();
    if (!validateApplications()) return;
    setBusy(true);
    try {
      await createUser({
        name: form.name.trim(),
        email: form.email.trim().toLowerCase(),
        role: form.role,
        active: form.active,
        send_invite: form.send_invite,
        applications: form.applications,
        application_role: form.application_role,
        ...accessPayload(form),
      });
      toast.success(
        form.send_invite ? "User created — invite email sent" : "User created",
      );
      setAddOpen(false);
      setForm(emptyForm);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to create user");
    } finally {
      setBusy(false);
    }
  };

  const submitEdit = async (e) => {
    e.preventDefault();
    if (!editing) return;
    if (!validateApplications()) return;
    setBusy(true);
    try {
      await updateUser(editing.id, {
        name: form.name.trim(),
        email: form.email.trim().toLowerCase(),
        role: form.role,
        active: form.active,
        applications: form.applications,
        application_role: form.application_role,
        ...accessPayload(form),
      });
      toast.success("User updated");
      setEditOpen(false);
      setEditing(null);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to update user");
    } finally {
      setBusy(false);
    }
  };

  // At least one application is required for non-admin users.
  const validateApplications = () => {
    if ((form.role || "").toLowerCase() === "admin") return true;
    if (!form.applications || form.applications.length === 0) {
      toast.error("Select at least one application.");
      return false;
    }
    return true;
  };

  const handleSendReset = async () => {
    if (!editing) return;
    setBusy(true);
    try {
      await sendUserReset(editing.id);
      toast.success(`Password reset link sent to ${editing.email}`);
    } catch {
      toast.error("Failed to send reset link");
    } finally {
      setBusy(false);
    }
  };

  // Table filtering by Business Client / SAP System (scales as WarehouseX grows).
  const filteredUsers = users.filter((u) => {
    if (filterClient !== NONE) {
      const ids = Array.isArray(u.allowedClientIds) ? u.allowedClientIds : [];
      if (!ids.includes(filterClient)) return false;
    }
    if (filterSap !== NONE) {
      const ids = Array.isArray(u.allowedSapSystems) ? u.allowedSapSystems : [];
      if (!ids.includes(filterSap)) return false;
    }
    return true;
  });

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-xl font-bold text-[#1F2937]" style={{ fontFamily: "Chivo" }}>Users</h1>
          <p className="text-[11px] text-[#6B7280] mt-0.5">
            Invite teammates and scope their access to a specific application. Passwords are user-managed via secure email links.
          </p>
        </div>
        <Button
          onClick={openAdd}
          className="bg-[#025979] hover:bg-[#03738C] text-white rounded-sm text-xs h-9"
          data-testid="add-user-btn"
        >
          <UserPlus className="w-3.5 h-3.5 mr-1.5" />
          Add User
        </Button>
      </div>
      {err && <p className="text-xs text-red-500 mb-3">{err}</p>}

      {/* Filters — by Business Client / SAP System */}
      {clients.length > 0 && (
        <div className="flex flex-wrap items-center gap-2 mb-3" data-testid="users-filter-bar">
          <span className="text-[11px] font-medium text-[#6B7280]">Filter:</span>
          <Select value={filterClient} onValueChange={setFilterClient}>
            <SelectTrigger className="rounded-sm text-xs h-8 w-52 bg-white" data-testid="filter-client-select">
              <SelectValue placeholder="All Business Clients" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={NONE}>All Business Clients</SelectItem>
              {clients.map((c) => (
                <SelectItem key={c.clientId} value={c.clientId} data-testid={`filter-client-${c.clientId}`}>{c.clientName}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={filterSap} onValueChange={setFilterSap}>
            <SelectTrigger className="rounded-sm text-xs h-8 w-52 bg-white" data-testid="filter-sap-select">
              <SelectValue placeholder="All SAP Systems" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={NONE}>All SAP Systems</SelectItem>
              {sapSystems.map((s) => (
                <SelectItem key={s.clientSapId} value={s.clientSapId} data-testid={`filter-sap-${s.clientSapId}`}>
                  <span className="inline-flex items-center gap-1.5">{sapLabel(sapSystems, s.clientSapId)} <EnvBadge env={s.environment} /> · {clientLabel(clients, s.clientId)}</span>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {(filterClient !== NONE || filterSap !== NONE) && (
            <button
              type="button"
              onClick={() => { setFilterClient(NONE); setFilterSap(NONE); }}
              className="text-[11px] text-[#025979] hover:underline"
              data-testid="filter-clear"
            >
              Clear
            </button>
          )}
        </div>
      )}

      {/* Users table */}
      <div className="bg-white border border-[#E5E7EB] rounded-sm shadow-sm overflow-x-auto">
        <table className="w-full text-xs min-w-[1180px]">
          <thead>
            <tr className="bg-[#F8FAFC] border-b border-[#E5E7EB]">
              <th className="text-left px-4 py-3 text-[#6B7280] font-medium">Name</th>
              <th className="text-left px-4 py-3 text-[#6B7280] font-medium">Email</th>
              <th className="text-left px-4 py-3 text-[#6B7280] font-medium w-28">Platform Role</th>
              <th className="text-left px-4 py-3 text-[#6B7280] font-medium w-56">Applications</th>
              <th className="text-left px-4 py-3 text-[#6B7280] font-medium w-32">Business Client</th>
              <th className="text-left px-4 py-3 text-[#6B7280] font-medium w-32">SAP System</th>
              <th className="text-left px-4 py-3 text-[#6B7280] font-medium w-24">Status</th>
              <th className="text-right px-4 py-3 text-[#6B7280] font-medium w-20">Actions</th>
            </tr>
          </thead>
          <tbody data-testid="users-table-body">
            {filteredUsers.length === 0 ? (
              <tr>
                <td colSpan={8} className="px-4 py-8 text-center text-[#9CA3AF]">
                  {users.length === 0 ? "No users yet." : "No users match the selected filters."}
                </td>
              </tr>
            ) : (
              filteredUsers.map((u) => (
                <tr key={u.id} className="border-b border-[#E5E7EB] last:border-b-0" data-testid={`user-row-${u.id}`}>
                  <td className="px-4 py-3 text-[#1F2937] font-medium">{u.name}</td>
                  <td className="px-4 py-3 text-[#4B5563]">{u.email}</td>
                  <td className="px-4 py-3">
                    <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-medium capitalize ${PLATFORM_ROLE_BADGE[u.role] || PLATFORM_ROLE_BADGE.consultant}`}>
                      {u.role}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    {(u.role || "").toLowerCase() === "admin" ? (
                      <span className="inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-medium bg-[#E0F2FE] text-[#025979] border border-[#025979]/20" data-testid={`user-apps-${u.id}`}>
                        All applications
                      </span>
                    ) : (
                      <div className="flex flex-wrap gap-1" data-testid={`user-apps-${u.id}`}>
                        {(Array.isArray(u.applications) && u.applications.length
                          ? u.applications
                          : [u.application_access || APP_WHX]
                        ).map((app) => (
                          <span
                            key={app}
                            className={`inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-medium ${APP_BADGE[app] || APP_BADGE[APP_WHX]}`}
                          >
                            {app}
                          </span>
                        ))}
                      </div>
                    )}
                  </td>
                  <td className="px-4 py-3 text-[#1F2937]" data-testid={`user-client-${u.id}`}>
                    {summarizeIds(u.allowedClientIds, (id) => clientLabel(clients, id)) || <span className="text-[#9CA3AF]">—</span>}
                  </td>
                  <td className="px-4 py-3 text-[#1F2937]" data-testid={`user-sap-${u.id}`}>
                    {summarizeIds(u.allowedSapSystems, (id) => sapLabel(sapSystems, id)) || <span className="text-[#9CA3AF]">—</span>}
                  </td>
                  <td className="px-4 py-3">
                    {u.active === false ? (
                      <span className="text-[11px] text-[#6B7280]">Inactive</span>
                    ) : u.pending_password ? (
                      <span className="text-[11px] text-[#92400E]">Invite pending</span>
                    ) : (
                      <span className="text-[11px] text-[#047857]">Active</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <button
                      type="button"
                      onClick={() => openEdit(u)}
                      className="inline-flex items-center gap-1 text-[#025979] hover:text-[#03738C] transition-colors"
                      data-testid={`edit-user-${u.id}`}
                    >
                      <Pencil className="w-3 h-3" />
                      Edit
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Add User dialog */}
      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent data-testid="add-user-dialog" className="max-h-[90vh] p-0 gap-0 flex flex-col overflow-hidden">
          <DialogHeader className="shrink-0 px-6 pt-6 pb-4 border-b border-[#E5E7EB] text-left pr-10">
            <DialogTitle>Invite a new user</DialogTitle>
            <DialogDescription>
              They will receive an email with a secure link to set their own password.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={submitAdd} className="flex flex-col min-h-0 flex-1">
            <div className="flex-1 min-h-0 overflow-y-auto px-6 py-4 space-y-4" data-testid="add-user-scroll-body">
            <Field label="Name" id="user-name">
              <Input id="user-name" data-testid="user-name" value={form.name} onChange={(e) => updateForm({ name: e.target.value })} className="rounded-sm" required />
            </Field>
            <Field label="Email" id="user-email">
              <Input id="user-email" data-testid="user-email" type="email" value={form.email} onChange={(e) => updateForm({ email: e.target.value })} className="rounded-sm" required />
            </Field>
            <Field label="Platform Role" id="user-role">
              <Select value={form.role} onValueChange={(v) => updateForm({ role: v })}>
                <SelectTrigger id="user-role" data-testid="user-role" className="rounded-sm text-xs h-10"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {PLATFORM_ROLE_OPTIONS.map((r) => (
                    <SelectItem key={r.value} value={r.value} data-testid={`role-option-${r.value}`}>{r.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>
            <Field label="Applications" id="user-applications">
              <ApplicationsField
                role={form.role}
                value={form.applications}
                onToggle={toggleApplication}
                idPrefix="add"
              />
            </Field>
            {smartEwmInForm(form) && (
              <Field label="Application Role (SmartEWM)" id="user-app-role">
                <Select value={form.application_role} onValueChange={(v) => updateForm({ application_role: v })}>
                  <SelectTrigger id="user-app-role" data-testid="user-app-role" className="rounded-sm text-xs h-10"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {SMARTEWM_ROLE_OPTIONS.map((r) => (
                      <SelectItem key={r} value={r} data-testid={`app-role-option-${r.toLowerCase()}`}>{r}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </Field>
            )}
            {showMultiClientAccess(form) && (
              <MultiClientAccessField
                key={`add-mc-${form.role}`}
                role={form.role}
                clients={clients}
                sapSystems={sapSystems}
                form={form}
                onChange={updateForm}
                idPrefix="add"
              />
            )}
            <ToggleRow
              label="Active"
              helper="Only active users can sign in."
              checked={form.active}
              onChange={(v) => updateForm({ active: v })}
              testId="user-active"
            />
            <label className="flex items-start gap-2 rounded-sm border border-[#E5E7EB] bg-[#F8FAFC] p-3 cursor-pointer">
              <Checkbox
                checked={form.send_invite}
                onCheckedChange={(v) => updateForm({ send_invite: !!v })}
                data-testid="user-send-invite"
                className="mt-0.5"
              />
              <span className="text-xs text-[#1F2937] leading-snug">
                Send invite email to set password
                <span className="block text-[11px] text-[#6B7280] mt-0.5">Contains a secure, single-use link valid for 24 hours.</span>
              </span>
            </label>
            </div>
            <DialogFooter className="shrink-0 px-6 py-4 border-t border-[#E5E7EB] bg-white sm:justify-end gap-2">
              <Button type="button" variant="outline" onClick={() => setAddOpen(false)} className="rounded-sm text-xs h-9" data-testid="add-user-cancel">Cancel</Button>
              <Button type="submit" disabled={busy} className="bg-[#025979] hover:bg-[#03738C] text-white rounded-sm text-xs h-9" data-testid="add-user-save">
                {busy ? "Saving…" : "Create user"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Edit User dialog */}
      <Dialog open={editOpen} onOpenChange={(v) => { setEditOpen(v); if (!v) setEditing(null); }}>
        <DialogContent data-testid="edit-user-dialog" className="max-h-[90vh] p-0 gap-0 flex flex-col overflow-hidden">
          <DialogHeader className="shrink-0 px-6 pt-6 pb-4 border-b border-[#E5E7EB] text-left pr-10">
            <DialogTitle data-testid="edit-user-title">{form.name ? `Edit user — ${form.name}` : "Edit user"}</DialogTitle>
            <DialogDescription>Update account details, role, and application access. Passwords are user-managed via secure email links.</DialogDescription>
          </DialogHeader>
          <form onSubmit={submitEdit} className="flex flex-col min-h-0 flex-1">
            <div className="flex-1 min-h-0 overflow-y-auto px-6 py-4 space-y-4" data-testid="edit-user-scroll-body">
            <Field label="Name" id="edit-name">
              <Input id="edit-name" data-testid="edit-user-name" value={form.name} onChange={(e) => updateForm({ name: e.target.value })} className="rounded-sm" required />
            </Field>
            <Field label="Email" id="edit-email">
              <Input id="edit-email" data-testid="edit-user-email" type="email" value={form.email} onChange={(e) => updateForm({ email: e.target.value })} className="rounded-sm" required />
            </Field>
            <Field label="Platform Role" id="edit-role">
              <Select value={form.role} onValueChange={(v) => updateForm({ role: v })}>
                <SelectTrigger id="edit-role" data-testid="edit-user-role" className="rounded-sm text-xs h-10"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {PLATFORM_ROLE_OPTIONS.map((r) => (
                    <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>
            <Field label="Applications" id="edit-applications">
              <ApplicationsField
                role={form.role}
                value={form.applications}
                onToggle={toggleApplication}
                idPrefix="edit"
              />
            </Field>
            {smartEwmInForm(form) && (
              <Field label="Application Role (SmartEWM)" id="edit-app-role">
                <Select value={form.application_role} onValueChange={(v) => updateForm({ application_role: v })}>
                  <SelectTrigger id="edit-app-role" data-testid="edit-user-app-role" className="rounded-sm text-xs h-10"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {SMARTEWM_ROLE_OPTIONS.map((r) => (
                      <SelectItem key={r} value={r}>{r}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </Field>
            )}
            {showMultiClientAccess(form) && (
              <MultiClientAccessField
                key={`edit-mc-${form.role}`}
                role={form.role}
                clients={clients}
                sapSystems={sapSystems}
                form={form}
                onChange={updateForm}
                idPrefix="edit"
              />
            )}
            <ToggleRow
              label="Active"
              helper="Inactive users cannot sign in."
              checked={form.active}
              onChange={(v) => updateForm({ active: v })}
              testId="edit-user-active"
            />
            <div className="rounded-sm border border-[#E5E7EB] bg-[#F8FAFC] p-3">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <p className="text-xs font-medium text-[#1F2937]">Password reset</p>
                  <p className="text-[11px] text-[#6B7280] mt-0.5 leading-snug">
                    Emails the user a secure link to choose a new password. The admin never sees or sets passwords.
                  </p>
                </div>
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleSendReset}
                  disabled={busy}
                  className="shrink-0 rounded-sm text-xs h-8 border-[#025979] text-[#025979] hover:bg-[#E0F2FE]"
                  data-testid="edit-user-send-reset"
                >
                  <Mail className="w-3 h-3 mr-1" />
                  Send Reset Link
                </Button>
              </div>
            </div>
            </div>
            <DialogFooter className="shrink-0 px-6 py-4 border-t border-[#E5E7EB] bg-white sm:justify-end gap-2">
              <Button type="button" variant="outline" onClick={() => setEditOpen(false)} className="rounded-sm text-xs h-9" data-testid="edit-user-cancel">Cancel</Button>
              <Button type="submit" disabled={busy} className="bg-[#025979] hover:bg-[#03738C] text-white rounded-sm text-xs h-9" data-testid="edit-user-save">
                {busy ? "Saving…" : "Save changes"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// True when the user's selected applications grant SmartEWM access (directly
// or via the WarehouseX Transformation umbrella).
function smartEwmInForm(form) {
  if ((form.role || "").toLowerCase() === "admin") return false;
  return form.applications.includes(APP_SMARTEWM) || form.applications.includes(APP_WHX);
}

// The Multi-Client access section is relevant for platform admins (who may be
// scoped to multiple clients/SAP systems) and for any SmartEWM-capable user.
function showMultiClientAccess(form) {
  if ((form.role || "").toLowerCase() === "admin") return true;
  return smartEwmInForm(form);
}

// Build the Multi-Client access payload sent to the API. Admins keep their
// full multi-select selection; everyone else is mirrored from the single
// Client / SAP System pickers (default == the only allowed value).
function accessPayload(form) {
  return {
    allowedClientIds: form.allowedClientIds || [],
    defaultClientId: form.defaultClientId || "",
    allowedSapSystems: form.allowedSapSystems || [],
    defaultSapSystem: form.defaultSapSystem || "",
  };
}

// Client + SAP System access scoping. Collapsible to keep the modal compact.
//  * Admins  -> advanced multi-select (Business Clients + SAP Systems +
//    defaults), COLLAPSED by default (admins rarely edit it; they bypass too).
//  * Others  -> simple single Client + single SAP System assignment, EXPANDED
//    by default (their access depends directly on it).
// All options show business-friendly names (PrimeS4 / SAP EWM DEV); IDs are
// stored internally and never surfaced.
function MultiClientAccessField({ role, clients, sapSystems, form, onChange, idPrefix }) {
  const isAdmin = (role || "").toLowerCase() === "admin";
  const [expanded, setExpanded] = useState(!isAdmin);

  const allowedClients = form.allowedClientIds || [];
  const visibleSystems = sapSystems.filter(
    (s) => allowedClients.length === 0 || allowedClients.includes(s.clientId),
  );

  if (clients.length === 0) {
    return (
      <div
        className="rounded-sm border border-dashed border-[#E5E7EB] bg-[#F8FAFC] p-3 text-[11px] text-[#6B7280] leading-snug"
        data-testid={`${idPrefix}-multiclient-empty`}
      >
        No WarehouseX clients are defined yet. Create a Business Client and SAP
        System under SmartEWM governance to assign access.
      </div>
    );
  }

  // Collapsed one-line summary of the current assignment.
  const summary = (() => {
    const cIds = allowedClients;
    const sIds = form.allowedSapSystems || [];
    if (cIds.length === 0 && sIds.length === 0) return "Not assigned (legacy / unscoped)";
    const cPart = cIds.length === 0 ? "No client"
      : cIds.length === 1 ? clientLabel(clients, cIds[0]) : `${cIds.length} clients`;
    const sPart = sIds.length === 0 ? "No SAP system"
      : sIds.length === 1 ? sapLabel(sapSystems, sIds[0]) : `${sIds.length} SAP systems`;
    return `${cPart} · ${sPart}`;
  })();

  // ---- simple-mode handlers (single client + single SAP system) ---------
  const onClient = (val) => {
    const cid = val === NONE ? "" : val;
    const sys = sapSystems.find((s) => s.clientSapId === form.defaultSapSystem);
    const keepSap = cid && sys && sys.clientId === cid ? form.defaultSapSystem : "";
    onChange({
      defaultClientId: cid,
      allowedClientIds: cid ? [cid] : [],
      defaultSapSystem: keepSap,
      allowedSapSystems: keepSap ? [keepSap] : [],
    });
  };
  const onSap = (val) => {
    const sid = val === NONE ? "" : val;
    onChange({ defaultSapSystem: sid, allowedSapSystems: sid ? [sid] : [] });
  };

  // ---- advanced-mode handlers (multi) -----------------------------------
  const toggleClient = (cid) => {
    const has = allowedClients.includes(cid);
    const next = has ? allowedClients.filter((c) => c !== cid) : [...allowedClients, cid];
    const nextSystems = (form.allowedSapSystems || []).filter((sid) => {
      const sys = sapSystems.find((s) => s.clientSapId === sid);
      return sys && next.includes(sys.clientId);
    });
    onChange({
      allowedClientIds: next,
      defaultClientId: next.includes(form.defaultClientId) ? form.defaultClientId : "",
      allowedSapSystems: nextSystems,
      defaultSapSystem: nextSystems.includes(form.defaultSapSystem) ? form.defaultSapSystem : "",
    });
  };
  const toggleSap = (sid) => {
    const cur = form.allowedSapSystems || [];
    const next = cur.includes(sid) ? cur.filter((s) => s !== sid) : [...cur, sid];
    onChange({
      allowedSapSystems: next,
      defaultSapSystem: next.includes(form.defaultSapSystem) ? form.defaultSapSystem : "",
    });
  };

  return (
    <div className="rounded-sm border border-[#E5E7EB] bg-[#F8FAFC]" data-testid={`${idPrefix}-multiclient-section`}>
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        className="flex w-full items-center justify-between gap-3 px-3 py-2.5 text-left"
        data-testid={`${idPrefix}-multiclient-toggle`}
        aria-expanded={expanded}
      >
        <span className="min-w-0">
          <span className="flex items-center gap-1.5 text-xs font-semibold text-[#025979]">
            {expanded ? <ChevronDown className="w-3.5 h-3.5 shrink-0" /> : <ChevronRight className="w-3.5 h-3.5 shrink-0" />}
            {isAdmin ? "Client & SAP Access (Advanced)" : "Client & SAP Access"}
          </span>
          {!expanded && (
            <span className="block pl-5 text-[11px] text-[#6B7280] truncate" data-testid={`${idPrefix}-multiclient-summary`}>
              {summary}
            </span>
          )}
        </span>
      </button>

      {expanded && !isAdmin && (
        <div className="space-y-3 px-3 pb-3 pt-1" data-testid={`${idPrefix}-multiclient-simple`}>
          <Field label="Client Assignment" id={`${idPrefix}-client`}>
            <Select value={form.defaultClientId || NONE} onValueChange={onClient}>
              <SelectTrigger id={`${idPrefix}-client`} data-testid={`${idPrefix}-client-select`} className="rounded-sm text-xs h-10 bg-white">
                <SelectValue placeholder="Select a business client" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={NONE} data-testid={`${idPrefix}-client-none`}>No client (legacy / unscoped)</SelectItem>
                {clients.map((c) => (
                  <SelectItem key={c.clientId} value={c.clientId} data-testid={`${idPrefix}-client-${c.clientId}`}>
                    <span className="inline-flex items-center gap-1.5"><ClientLogo logo={c.logo} name={c.clientName} size={16} />{c.clientName}</span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>
          <Field label="SAP System Assignment" id={`${idPrefix}-sap`}>
            <Select value={form.defaultSapSystem || NONE} onValueChange={onSap} disabled={!form.defaultClientId}>
              <SelectTrigger id={`${idPrefix}-sap`} data-testid={`${idPrefix}-sap-select`} className="rounded-sm text-xs h-10 bg-white">
                <SelectValue placeholder={form.defaultClientId ? "Select a SAP system" : "Select a client first"} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={NONE} data-testid={`${idPrefix}-sap-none`}>No SAP system</SelectItem>
                {visibleSystems.map((s) => (
                  <SelectItem key={s.clientSapId} value={s.clientSapId} data-testid={`${idPrefix}-sap-${s.clientSapId}`}>
                    <span className="inline-flex items-center gap-1.5">{sapLabel(sapSystems, s.clientSapId)} <EnvBadge env={s.environment} /></span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>
          <p className="text-[11px] text-[#6B7280] leading-snug">
            Restricts this user to the selected client &amp; SAP system. Leave blank
            to keep legacy unscoped access.
          </p>
        </div>
      )}

      {expanded && isAdmin && (
        <div className="space-y-3 px-3 pb-3 pt-1" data-testid={`${idPrefix}-multiclient-advanced`}>
          <Field label="Business Clients" id={`${idPrefix}-allowed-clients`}>
            <div className="space-y-1.5" data-testid={`${idPrefix}-allowed-clients-group`}>
              {clients.map((c) => {
                const checked = allowedClients.includes(c.clientId);
                return (
                  <label
                    key={c.clientId}
                    className={`flex items-center gap-2.5 rounded-sm border px-3 py-2 cursor-pointer ${checked ? "border-[#025979]/40 bg-[#F0F9FF]" : "border-[#E5E7EB] bg-white"}`}
                  >
                    <Checkbox checked={checked} onCheckedChange={() => toggleClient(c.clientId)} data-testid={`${idPrefix}-allowed-client-${c.clientId}`} />
                    <span className="text-xs font-medium text-[#1F2937] inline-flex items-center gap-2"><ClientLogo logo={c.logo} name={c.clientName} size={18} />{c.clientName}</span>
                  </label>
                );
              })}
            </div>
          </Field>
          <Field label="Default Client" id={`${idPrefix}-default-client`}>
            <Select value={form.defaultClientId || NONE} onValueChange={(v) => onChange({ defaultClientId: v === NONE ? "" : v })} disabled={allowedClients.length === 0}>
              <SelectTrigger id={`${idPrefix}-default-client`} data-testid={`${idPrefix}-default-client-select`} className="rounded-sm text-xs h-10 bg-white">
                <SelectValue placeholder="None" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={NONE}>None</SelectItem>
                {allowedClients.map((cid) => (
                  <SelectItem key={cid} value={cid}>
                    <span className="inline-flex items-center gap-1.5"><ClientLogo logo={(clients.find((c) => c.clientId === cid) || {}).logo} name={clientLabel(clients, cid)} size={16} />{clientLabel(clients, cid)}</span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>
          <Field label="SAP Systems" id={`${idPrefix}-allowed-sap`}>
            {visibleSystems.length === 0 ? (
              <p className="text-[11px] text-[#6B7280]" data-testid={`${idPrefix}-allowed-sap-empty`}>
                Select at least one business client to choose its SAP systems.
              </p>
            ) : (
              <div className="space-y-1.5" data-testid={`${idPrefix}-allowed-sap-group`}>
                {visibleSystems.map((s) => {
                  const checked = (form.allowedSapSystems || []).includes(s.clientSapId);
                  return (
                    <label
                      key={s.clientSapId}
                      className={`flex items-center gap-2.5 rounded-sm border px-3 py-2 cursor-pointer ${checked ? "border-[#025979]/40 bg-[#F0F9FF]" : "border-[#E5E7EB] bg-white"}`}
                    >
                      <Checkbox checked={checked} onCheckedChange={() => toggleSap(s.clientSapId)} data-testid={`${idPrefix}-allowed-sap-${s.clientSapId}`} />
                      <span className="text-xs font-medium text-[#1F2937] inline-flex items-center gap-1.5">{sapLabel(sapSystems, s.clientSapId)} <EnvBadge env={s.environment} /> <span className="text-[10px] font-normal text-[#6B7280]">· {clientLabel(clients, s.clientId)}</span></span>
                    </label>
                  );
                })}
              </div>
            )}
          </Field>
          <Field label="Default SAP System" id={`${idPrefix}-default-sap`}>
            <Select value={form.defaultSapSystem || NONE} onValueChange={(v) => onChange({ defaultSapSystem: v === NONE ? "" : v })} disabled={(form.allowedSapSystems || []).length === 0}>
              <SelectTrigger id={`${idPrefix}-default-sap`} data-testid={`${idPrefix}-default-sap-select`} className="rounded-sm text-xs h-10 bg-white">
                <SelectValue placeholder="None" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={NONE}>None</SelectItem>
                {(form.allowedSapSystems || []).map((sid) => (
                  <SelectItem key={sid} value={sid}>{sapLabel(sapSystems, sid)}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>
          <p className="text-[11px] text-[#6B7280] leading-snug">
            Admins bypass enforcement, but these assignments seed their default
            working client &amp; SAP system. Leave blank for no default.
          </p>
        </div>
      )}
    </div>
  );
}

// Multi-select application access control. WarehouseX Transformation is the
// full platform — selecting it auto-selects and locks the child apps. Admins
// always have every application, so the control collapses to an info note.
function ApplicationsField({ role, value, onToggle, idPrefix }) {
  const isAdmin = (role || "").toLowerCase() === "admin";
  if (isAdmin) {
    return (
      <div
        className="rounded-sm border border-[#E5E7EB] bg-[#F8FAFC] p-3 text-[11px] text-[#4B5563] leading-snug"
        data-testid={`${idPrefix}-applications-admin-note`}
      >
        Admin users have access to all applications. No restrictions apply.
      </div>
    );
  }
  const whxSelected = value.includes(APP_WHX);
  return (
    <div className="space-y-1.5" data-testid={`${idPrefix}-applications-group`}>
      {APPLICATION_CATALOGUE.map((app) => {
        const isChild = WHX_CHILD_APPS.includes(app);
        const lockedByWhx = whxSelected && isChild;
        const checked = value.includes(app) || lockedByWhx;
        return (
          <label
            key={app}
            className={`flex items-center gap-2.5 rounded-sm border px-3 py-2 ${
              checked ? "border-[#025979]/40 bg-[#F0F9FF]" : "border-[#E5E7EB]"
            } ${lockedByWhx ? "cursor-not-allowed opacity-70" : "cursor-pointer"}`}
          >
            <Checkbox
              checked={checked}
              disabled={lockedByWhx}
              onCheckedChange={() => onToggle(app)}
              data-testid={`${idPrefix}-application-${appSlug(app)}`}
            />
            <span className="text-xs font-medium text-[#1F2937]">
              {app}
              {app === APP_WHX && (
                <span className="ml-1.5 text-[10px] font-normal text-[#6B7280]">Full platform</span>
              )}
            </span>
          </label>
        );
      })}
      {whxSelected && (
        <p
          className="text-[11px] text-[#025979] pt-0.5"
          data-testid={`${idPrefix}-applications-whx-note`}
        >
          WarehouseX Transformation includes all WarehouseX applications.
        </p>
      )}
    </div>
  );
}

function Field({ label, id, children }) {
  return (
    <div>
      <Label htmlFor={id} className="text-xs font-medium text-[#1F2937] mb-1.5 block">{label}</Label>
      {children}
    </div>
  );
}

function ToggleRow({ label, helper, checked, onChange, testId }) {
  return (
    <div className="flex items-center justify-between rounded-sm border border-[#E5E7EB] px-3 py-2.5">
      <div>
        <p className="text-xs font-medium text-[#1F2937]">{label}</p>
        {helper && <p className="text-[11px] text-[#6B7280]">{helper}</p>}
      </div>
      <Switch checked={checked} onCheckedChange={onChange} data-testid={testId} />
    </div>
  );
}
